import {
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  differenceInCalendarDays,
  format,
  parseISO
} from 'date-fns'
import {
  useParams
} from 'react-router-dom'
import {
  BedDouble,
  CalendarDays,
  Check,
  ChevronDown,
  Download,
  MapPin,
  Plane,
  Sparkles,
  X
} from 'lucide-react'
import {
  getTripsApiV1TripsTokenGet
} from '../client'
import type {
  TripFlightBooking as ApiFlightBooking,
  TripFlightLeg as ApiFlightLeg,
  TripsPageResponse,
  TripStayBooking as ApiStayBooking
} from '../client'
import {
  API_BASE_URL
} from '../config'
import {
  formatDuration
} from '../utils/flightOptions'
import {
  CatchLogo
} from './CatchLogo'

type Period = 'upcoming' | 'past'
type BookingFilter = 'all' | 'flight' | 'stay'

interface AssistantIdentity {
  name: string
  pictureUrl?: string | undefined
}

interface FlightLeg {
  label: string
  date: string
  origin: string
  originCity: string
  destination: string
  destinationCity: string
  departingAt: string
  arrivingAt: string
  duration: string
  flightNumber: string
}

interface FlightBooking {
  id: string
  kind: 'flight'
  status: 'confirmed' | 'cancelled'
  airline: string
  airlineIata: string
  airlineLogoUrl?: string | undefined
  cabin: string
  confirmation: string
  passengers: string
  baggage: string
  price: string
  refundability: string
  legs: FlightLeg[]
}

interface StayPhoto {
  src: string
  alt: string
}

interface StayBooking {
  id: string
  kind: 'stay'
  status: 'confirmed' | 'cancelled'
  hotel: string
  location: string
  checkin: string
  checkout: string
  nights: number
  room: string
  guests: string
  board: string
  confirmation: string
  cancellation: string
  price: string
  photos: StayPhoto[]
}

type Booking = FlightBooking | StayBooking

interface Trip {
  id: string
  destination: string
  country: string
  dateRange: string
  startsIn?: string | undefined
  assistantNote: string
  period: Period
  bookings: Booking[]
}

interface TripsPageData {
  ownerFirstName?: string | undefined
  assistant: AssistantIdentity
  trips: Trip[]
}

const HOTEL_PHOTOS = {
  paris: [
    {
      src: 'https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=1400&q=85',
      alt: 'The booked king room at Hôtel Dame des Arts'
    },
    {
      src: 'https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=900&q=85',
      alt: 'Seating area in the booked room'
    },
    {
      src: 'https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&w=900&q=85',
      alt: 'Bathroom in the booked room'
    }
  ],
  newYork: [
    {
      src: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?auto=format&fit=crop&w=1400&q=85',
      alt: 'The booked studio room at The Ludlow Hotel'
    },
    {
      src: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=900&q=85',
      alt: 'Window and lounge area in the booked room'
    },
    {
      src: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?auto=format&fit=crop&w=900&q=85',
      alt: 'Bathroom in the booked room'
    }
  ],
  london: [
    {
      src: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?auto=format&fit=crop&w=1400&q=85',
      alt: 'The booked room at The Hoxton, Holborn'
    },
    {
      src: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?auto=format&fit=crop&w=900&q=85',
      alt: 'Desk in the booked room'
    },
    {
      src: 'https://images.unsplash.com/photo-1591088398332-8a7791972843?auto=format&fit=crop&w=900&q=85',
      alt: 'Bathroom in the booked room'
    }
  ]
} satisfies Record<string, [StayPhoto, ...StayPhoto[]]>

const TRIPS: Trip[] = [
  {
    id: 'paris',
    destination: 'Paris',
    country: 'France',
    dateRange: 'Sep 3–7, 2026',
    startsIn: 'In 4 days',
    assistantNote: 'I kept this one simple: direct flights and a room right by Saint-Germain.',
    period: 'upcoming',
    bookings: [
      {
        id: 'paris-flight',
        kind: 'flight',
        status: 'confirmed',
        airline: 'Air France',
        airlineIata: 'AF',
        cabin: 'Economy Flex',
        confirmation: 'K7H2QP',
        passengers: 'Yoav Ramon',
        baggage: '1 checked bag · 1 carry-on',
        price: '€642',
        refundability: 'Refundable',
        legs: [
          {
            label: 'Outbound',
            date: 'Thu, Sep 3',
            origin: 'TLV',
            originCity: 'Tel Aviv',
            destination: 'CDG',
            destinationCity: 'Paris',
            departingAt: '07:45',
            arrivingAt: '11:35',
            duration: '4h 50m · Direct',
            flightNumber: 'AF 963'
          },
          {
            label: 'Return',
            date: 'Mon, Sep 7',
            origin: 'CDG',
            originCity: 'Paris',
            destination: 'TLV',
            destinationCity: 'Tel Aviv',
            departingAt: '18:20',
            arrivingAt: '23:40',
            duration: '4h 20m · Direct',
            flightNumber: 'AF 962'
          }
        ]
      },
      {
        id: 'paris-stay',
        kind: 'stay',
        status: 'confirmed',
        hotel: 'Hôtel Dame des Arts',
        location: '6th arrondissement · 4 Rue Danton',
        checkin: 'Thu, Sep 3 · from 3:00 PM',
        checkout: 'Mon, Sep 7 · by 12:00 PM',
        nights: 4,
        room: 'Signature King, Eiffel Tower view',
        guests: '1 guest',
        board: 'Breakfast included',
        confirmation: 'HDA-829104',
        cancellation: 'Free cancellation until Sep 1 at 6:00 PM',
        price: '€1,248',
        photos: HOTEL_PHOTOS.paris
      }
    ]
  },
  {
    id: 'new-york',
    destination: 'New York',
    country: 'United States',
    dateRange: 'Oct 12–17, 2026',
    startsIn: 'In 6 weeks',
    assistantNote: 'I lined up the overnight flight and a downtown stay, so the trip flows without extra stops.',
    period: 'upcoming',
    bookings: [
      {
        id: 'new-york-flight',
        kind: 'flight',
        status: 'confirmed',
        airline: 'United Airlines',
        airlineIata: 'UA',
        cabin: 'Premium Economy',
        confirmation: 'M4Q9LX',
        passengers: 'Yoav Ramon',
        baggage: '2 checked bags · 1 carry-on',
        price: '$1,460',
        refundability: 'Refundable',
        legs: [
          {
            label: 'Outbound',
            date: 'Mon, Oct 12',
            origin: 'TLV',
            originCity: 'Tel Aviv',
            destination: 'EWR',
            destinationCity: 'Newark',
            departingAt: '23:30',
            arrivingAt: '05:10',
            duration: '12h 40m · Direct',
            flightNumber: 'UA 091'
          },
          {
            label: 'Return',
            date: 'Sat, Oct 17',
            origin: 'EWR',
            originCity: 'Newark',
            destination: 'TLV',
            destinationCity: 'Tel Aviv',
            departingAt: '15:15',
            arrivingAt: '08:45',
            duration: '10h 30m · Direct',
            flightNumber: 'UA 090'
          }
        ]
      },
      {
        id: 'new-york-stay',
        kind: 'stay',
        status: 'confirmed',
        hotel: 'The Ludlow Hotel',
        location: 'Lower East Side · 180 Ludlow St',
        checkin: 'Tue, Oct 13 · from 3:00 PM',
        checkout: 'Sat, Oct 17 · by 12:00 PM',
        nights: 4,
        room: 'Skybox Loft, city view',
        guests: '1 guest',
        board: 'Room only',
        confirmation: 'LUD-441920',
        cancellation: 'Free cancellation until Oct 10 at 11:59 PM',
        price: '$2,080',
        photos: HOTEL_PHOTOS.newYork
      }
    ]
  },
  {
    id: 'london',
    destination: 'London',
    country: 'United Kingdom',
    dateRange: 'Jun 12–15, 2026',
    assistantNote: 'All wrapped up. I kept the details here in case you need a receipt or confirmation.',
    period: 'past',
    bookings: [
      {
        id: 'london-flight',
        kind: 'flight',
        status: 'confirmed',
        airline: 'EL AL',
        airlineIata: 'LY',
        cabin: 'Economy',
        confirmation: 'Q8TF2C',
        passengers: 'Yoav Ramon',
        baggage: '1 checked bag · 1 carry-on',
        price: '$518',
        refundability: 'Non-refundable',
        legs: [
          {
            label: 'Outbound',
            date: 'Fri, Jun 12',
            origin: 'TLV',
            originCity: 'Tel Aviv',
            destination: 'LHR',
            destinationCity: 'London',
            departingAt: '08:15',
            arrivingAt: '11:45',
            duration: '5h 30m · Direct',
            flightNumber: 'LY 315'
          },
          {
            label: 'Return',
            date: 'Mon, Jun 15',
            origin: 'LHR',
            originCity: 'London',
            destination: 'TLV',
            destinationCity: 'Tel Aviv',
            departingAt: '21:20',
            arrivingAt: '04:15',
            duration: '4h 55m · Direct',
            flightNumber: 'LY 318'
          }
        ]
      },
      {
        id: 'london-stay',
        kind: 'stay',
        status: 'confirmed',
        hotel: 'The Hoxton, Holborn',
        location: 'Holborn · 199–206 High Holborn',
        checkin: 'Fri, Jun 12 · 3:00 PM',
        checkout: 'Mon, Jun 15 · 12:00 PM',
        nights: 3,
        room: 'Roomy, king bed',
        guests: '1 guest',
        board: 'Breakfast included',
        confirmation: 'HOX-221885',
        cancellation: 'Stay completed',
        price: '£735',
        photos: HOTEL_PHOTOS.london
      }
    ]
  }
]

const MOCK_PAGE_DATA: TripsPageData = {
  ownerFirstName: 'Yoav',
  assistant: {
    name: 'Uta',
    pictureUrl: 'https://catchagent.ai/images/assistants/uta.webp'
  },
  trips: TRIPS
}

function formatMoney(amount?: number | null, currency?: string): string {
  if (amount == null || !currency) {
    return 'Not available'
  }
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2
    }).format(amount)
  } catch {
    return `${amount.toLocaleString()} ${currency}`
  }
}

function formatCountry(country?: string | null): string {
  if (!country) {
    return ''
  }
  if (country.length !== 2) {
    return country
  }
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country.toUpperCase()) ?? country
  } catch {
    return country.toUpperCase()
  }
}

function formatDate(value?: string | null, pattern = 'EEE, MMM d'): string {
  if (!value) {
    return 'Date to be confirmed'
  }
  return format(parseISO(value), pattern)
}

function formatTime(value?: string | null): string {
  if (!value) {
    return 'TBD'
  }
  return format(parseISO(value), 'HH:mm')
}

function formatUtcDeadline(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZone: 'UTC',
    timeZoneName: 'short',
    year: 'numeric'
  }).format(parseISO(value))
}

function formatDateRange(start: string, end: string): string {
  const startDate = parseISO(start)
  const endDate = parseISO(end)
  if (start === end) {
    return format(startDate, 'MMM d, yyyy')
  }
  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${format(startDate, 'MMM d')}–${format(endDate, 'd, yyyy')}`
  }
  return `${format(startDate, 'MMM d, yyyy')}–${format(endDate, 'MMM d, yyyy')}`
}

function startsIn(start: string, period: Period): string | undefined {
  if (period === 'past') {
    return undefined
  }
  const days = differenceInCalendarDays(parseISO(start), new Date())
  if (days < 0) {
    return 'Happening now'
  }
  if (days === 0) {
    return 'Today'
  }
  if (days === 1) {
    return 'Tomorrow'
  }
  if (days < 14) {
    return `In ${days} days`
  }
  const weeks = Math.round(days / 7)
  return `In ${weeks} week${weeks === 1 ? '' : 's'}`
}

function flightNumbers(leg: ApiFlightLeg): string {
  return (leg.segments ?? [])
    .map((segment) => segment.flight_number)
    .filter((number): number is string => Boolean(number))
    .join(' · ')
}

function mapFlight(booking: ApiFlightBooking): FlightBooking {
  const firstSegment = booking.legs?.[0]?.segments?.[0]
  const airline = booking.airline ?? firstSegment?.airline_name ?? 'Flight'
  const airlineIata = booking.airline_iata ?? firstSegment?.airline_iata ?? ''
  const passengerNames = booking.passenger_names ?? []
  const passengerFallback = `${booking.passenger_count} passenger${booking.passenger_count === 1 ? '' : 's'}`
  const legs = (booking.legs ?? []).map((leg) => {
    const stops = leg.stops === 0 ? 'Direct' : `${leg.stops} stop${leg.stops === 1 ? '' : 's'}`
    return {
      label: leg.label,
      date: formatDate(leg.departing_at),
      origin: leg.origin_iata ?? 'TBD',
      originCity: leg.origin_city ?? '',
      destination: leg.destination_iata ?? 'TBD',
      destinationCity: leg.destination_city ?? '',
      departingAt: formatTime(leg.departing_at),
      arrivingAt: formatTime(leg.arriving_at),
      duration: [formatDuration(leg.duration), stops].filter(Boolean).join(' · '),
      flightNumber: flightNumbers(leg)
    }
  })
  if (legs.length === 0 && booking.departure_date) {
    legs.push({
      label: booking.return_date ? 'Outbound' : 'Flight',
      date: formatDate(booking.departure_date),
      origin: booking.origin_iata ?? 'TBD',
      originCity: '',
      destination: booking.destination_iata ?? 'TBD',
      destinationCity: '',
      departingAt: 'TBD',
      arrivingAt: 'TBD',
      duration: 'See confirmation for schedule',
      flightNumber: ''
    })
    if (booking.return_date) {
      legs.push({
        label: 'Return',
        date: formatDate(booking.return_date),
        origin: booking.destination_iata ?? 'TBD',
        originCity: '',
        destination: booking.origin_iata ?? 'TBD',
        destinationCity: '',
        departingAt: 'TBD',
        arrivingAt: 'TBD',
        duration: 'See confirmation for schedule',
        flightNumber: ''
      })
    }
  }
  return {
    id: booking.id,
    kind: 'flight',
    status: booking.status,
    airline,
    airlineIata,
    airlineLogoUrl: booking.airline_logo_url ?? firstSegment?.airline_logo_url ?? undefined,
    cabin: [booking.cabin_class, booking.fare_brand_name].filter(Boolean).join(' · ') || 'Cabin to be confirmed',
    confirmation: booking.booking_reference ?? 'Not provided',
    passengers: passengerNames.length > 0 ? passengerNames.join(', ') : passengerFallback,
    baggage: booking.baggage_summary ?? 'Confirm with airline',
    price: formatMoney(booking.price, booking.currency),
    refundability: booking.refundability_summary,
    legs
  }
}

function cancellationText(booking: ApiStayBooking): string {
  if (booking.status === 'cancelled') {
    return 'Cancelled'
  }
  if (booking.free_cancellation_until) {
    return `Free cancellation until ${formatUtcDeadline(booking.free_cancellation_until)}`
  }
  return booking.cancellation_summary
}

function mapStay(booking: ApiStayBooking): StayBooking {
  const guests = `${booking.guests} guest${booking.guests === 1 ? '' : 's'}`
  const checkinDate = formatDate(booking.checkin)
  const checkoutDate = formatDate(booking.checkout)
  return {
    id: booking.id,
    kind: 'stay',
    status: booking.status,
    hotel: booking.hotel_name ?? booking.property_type ?? 'Your stay',
    location: booking.address ?? (
      [booking.city, formatCountry(booking.country)].filter(Boolean).join(', ') || 'Location to be confirmed'
    ),
    checkin: `${checkinDate}${booking.checkin_time ? ` · from ${booking.checkin_time}` : ''}`,
    checkout: `${checkoutDate}${booking.checkout_time ? ` · by ${booking.checkout_time}` : ''}`,
    nights: booking.nights ?? 0,
    room: booking.room_name ?? 'Room details to be confirmed',
    guests,
    board: booking.board_name ?? 'Room only',
    confirmation: booking.booking_reference ?? 'Not provided',
    cancellation: cancellationText(booking),
    price: formatMoney(booking.price, booking.currency),
    photos: (booking.photos ?? []).map((photo) => ({
      src: photo.url,
      alt: photo.caption
    }))
  }
}

function isApiFlightBooking(
  booking: ApiFlightBooking | ApiStayBooking
): booking is ApiFlightBooking {
  return booking.kind === 'flight' || 'legs' in booking
}

function adaptTripsPage(data: TripsPageResponse): TripsPageData {
  return {
    ownerFirstName: data.owner_first_name ?? undefined,
    assistant: {
      name: data.assistant.name,
      pictureUrl: data.assistant.picture_url ?? undefined
    },
    trips: data.trips.map((trip) => ({
      id: trip.id,
      destination: trip.destination,
      country: formatCountry(trip.country),
      dateRange: formatDateRange(trip.start_date, trip.end_date),
      startsIn: startsIn(trip.start_date, trip.period),
      assistantNote: trip.assistant_note,
      period: trip.period,
      bookings: trip.bookings.map((booking) => isApiFlightBooking(booking)
        ? mapFlight(booking)
        : mapStay(booking))
    }))
  }
}

function AirlineLogo({
  iata,
  airline,
  logoUrl
}: {
  iata: string
  airline: string
  logoUrl?: string | undefined
}): JSX.Element {
  const [failed, setFailed] = useState(false)
  const fallbackSrc = iata
    ? `https://assets.duffel.com/img/airlines/for-light-background/full-color-logo/${iata}.svg`
    : undefined
  const src = logoUrl ?? fallbackSrc

  return (
    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-catch-beige-dark bg-catch-white">
      {failed || !src
        ? <span className="text-xs font-semibold text-gray-500">{iata || airline.slice(0, 2).toUpperCase()}</span>
        : (
          <img
            src={src}
            alt={`${airline} logo`}
            className="h-8 w-8 object-contain"
            onError={() => setFailed(true)}
          />
          )}
    </div>
  )
}

function DetailRow({ label, value }: { label: string, value: string }): JSX.Element {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-[0.12em] text-gray-400">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-catch-black">{value}</dd>
    </div>
  )
}

function AssistantAvatar({
  assistant,
  size = 'sm'
}: {
  assistant: AssistantIdentity
  size?: 'sm' | 'md'
}): JSX.Element {
  const [imageFailed, setImageFailed] = useState(false)

  return (
    <div
      aria-hidden="true"
      className={`flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-catch-red text-white shadow-soft ring-2 ring-white ${
        size === 'md' ? 'h-11 w-11' : 'h-7 w-7'
      }`}
    >
      {imageFailed || !assistant.pictureUrl
        ? (
          <span className={size === 'md' ? 'font-display text-base font-medium' : 'text-[11px] font-semibold'}>
            {assistant.name.slice(0, 1).toUpperCase()}
          </span>
          )
        : (
          <img
            src={assistant.pictureUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setImageFailed(true)}
          />
          )}
    </div>
  )
}

function FlightCard({
  assistant,
  booking
}: {
  assistant: AssistantIdentity
  booking: FlightBooking
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className="overflow-hidden rounded-2xl border border-catch-beige-dark bg-white shadow-soft">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <AirlineLogo
              iata={booking.airlineIata}
              airline={booking.airline}
              logoUrl={booking.airlineLogoUrl}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-catch-red/10 px-2 py-0.5 text-[11px] font-medium text-catch-red">
                  Flight
                </span>
                <span className={`flex items-center gap-1 text-[11px] font-medium ${
                  booking.status === 'confirmed' ? 'text-green-700' : 'text-gray-500'
                }`}>
                  {booking.status === 'confirmed'
                    ? <Check className="h-3 w-3" />
                    : <X className="h-3 w-3" />}
                  {booking.status === 'confirmed' ? 'Confirmed' : 'Cancelled'}
                </span>
              </div>
              <h3 className="mt-1 truncate font-display text-lg font-medium">
                {booking.airline}
              </h3>
              <p className="text-xs text-gray-500">{booking.cabin}</p>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[11px] uppercase tracking-[0.12em] text-gray-400">
              Booking ref
            </div>
            <div className="mt-1 font-mono text-sm font-semibold tracking-wider">
              {booking.confirmation}
            </div>
          </div>
        </div>

        <div className="mt-5 divide-y divide-catch-beige-dark">
          {booking.legs.map((leg) => (
            <div key={`${leg.origin}-${leg.destination}`} className="py-4 first:pt-0 last:pb-0">
              <div className="mb-3 flex items-center justify-between gap-3 text-xs">
                <span className="font-medium text-gray-600">{leg.label} · {leg.date}</span>
                <span className="text-gray-400">{leg.flightNumber}</span>
              </div>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div>
                  <div className="font-display text-2xl font-medium tabular-nums">
                    {leg.departingAt}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold">{leg.origin}</div>
                  <div className="text-xs text-gray-500">{leg.originCity}</div>
                </div>
                <div className="min-w-[110px] text-center sm:min-w-[180px]">
                  <div className="text-[11px] text-gray-400">{leg.duration}</div>
                  <div className="relative my-2 h-px bg-catch-beige-dark">
                    <span className="absolute -left-0.5 -top-1 h-2 w-2 rounded-full bg-catch-red" />
                    <Plane className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rotate-90 bg-white px-0.5 text-catch-red" />
                    <span className="absolute -right-0.5 -top-1 h-2 w-2 rounded-full bg-catch-red" />
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-medium tabular-nums">
                    {leg.arrivingAt}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold">{leg.destination}</div>
                  <div className="text-xs text-gray-500">{leg.destinationCity}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-catch-beige-dark bg-catch-grey/60">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium hover:bg-catch-grey sm:px-5"
        >
          <span>{expanded ? 'Hide booking details' : 'Booking details'}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && (
          <div className="grid grid-cols-2 gap-4 border-t border-catch-beige-dark px-4 py-4 animate-fade-in sm:grid-cols-4 sm:px-5">
            <DetailRow label="Passenger" value={booking.passengers} />
            <DetailRow label="Baggage" value={booking.baggage} />
            <DetailRow label="Total" value={booking.price} />
            <DetailRow label="Fare" value={booking.refundability} />
            <DetailRow label="Booked for you by" value={assistant.name} />
          </div>
        )}
      </div>
    </article>
  )
}

function PhotoGrid({ booking, onOpen }: { booking: StayBooking, onOpen: () => void }): JSX.Element | null {
  const firstPhoto = booking.photos[0]
  if (!firstPhoto) {
    return null
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`View room photos for ${booking.hotel}`}
      className="group relative grid h-52 w-full grid-cols-[1.55fr_1fr] gap-1 overflow-hidden text-left sm:h-64"
    >
      <img
        src={firstPhoto.src}
        alt={firstPhoto.alt}
        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
      />
      <div className="grid min-w-0 grid-rows-2 gap-1">
        {booking.photos.slice(1, 3).map((photo) => (
          <img
            key={photo.src}
            src={photo.src}
            alt={photo.alt}
            className="h-full min-h-0 w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
        ))}
      </div>
      <span className="absolute bottom-3 right-3 rounded-full bg-white/95 px-3 py-1.5 text-xs font-medium text-catch-black shadow-soft backdrop-blur">
        View room photos
      </span>
    </button>
  )
}

function StayCard({
  assistant,
  booking,
  onOpenGallery
}: {
  assistant: AssistantIdentity
  booking: StayBooking
  onOpenGallery: () => void
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className="overflow-hidden rounded-2xl border border-catch-beige-dark bg-white shadow-soft">
      <PhotoGrid booking={booking} onOpen={onOpenGallery} />
      <div className="p-4 sm:p-5">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-catch-red/10 px-2 py-0.5 text-[11px] font-medium text-catch-red">
                Stay
              </span>
              <span className={`flex items-center gap-1 text-[11px] font-medium ${
                booking.status === 'confirmed' ? 'text-green-700' : 'text-gray-500'
              }`}>
                {booking.status === 'confirmed'
                  ? <Check className="h-3 w-3" />
                  : <X className="h-3 w-3" />}
                {booking.status === 'confirmed' ? 'Confirmed' : 'Cancelled'}
              </span>
            </div>
            <h3 className="mt-2 font-display text-xl font-medium">{booking.hotel}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
              <MapPin className="h-3.5 w-3.5 shrink-0" /> {booking.location}
            </p>
          </div>
          <div className="shrink-0 sm:text-right">
            <div className="text-[11px] uppercase tracking-[0.12em] text-gray-400">
              Booking ref
            </div>
            <div className="mt-1 font-mono text-sm font-semibold tracking-wider">
              {booking.confirmation}
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 rounded-xl border border-catch-beige-dark bg-catch-grey/55 p-4 sm:grid-cols-2">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
              <CalendarDays className="h-4 w-4 text-catch-red" /> Check-in
            </div>
            <p className="mt-1 text-sm font-medium">{booking.checkin}</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
              <CalendarDays className="h-4 w-4 text-catch-red" /> Check-out
            </div>
            <p className="mt-1 text-sm font-medium">{booking.checkout}</p>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-catch-grey text-catch-red">
            <BedDouble className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium">{booking.room}</p>
            <p className="mt-0.5 text-sm text-gray-500">
              {booking.guests} · {booking.board}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2.5 text-xs font-medium text-green-800">
          <Check className="h-4 w-4 shrink-0" /> {booking.cancellation}
        </div>
      </div>

      <div className="border-t border-catch-beige-dark bg-catch-grey/60">
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium hover:bg-catch-grey sm:px-5"
        >
          <span>{expanded ? 'Hide stay details' : 'Stay details'}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
        {expanded && (
          <div className="grid grid-cols-2 gap-4 border-t border-catch-beige-dark px-4 py-4 animate-fade-in sm:grid-cols-4 sm:px-5">
            <DetailRow label="Length" value={`${booking.nights} nights`} />
            <DetailRow label="Guests" value={booking.guests} />
            <DetailRow label="Total" value={booking.price} />
            <DetailRow label="Booked for you by" value={assistant.name} />
          </div>
        )}
      </div>
    </article>
  )
}

function FilterButton({
  active,
  children,
  onClick
}: {
  active: boolean
  children: React.ReactNode
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'border-catch-red bg-catch-red text-white'
          : 'border-catch-beige-dark bg-white text-gray-600 hover:border-catch-red/50 hover:text-catch-black'
      }`}
    >
      {children}
    </button>
  )
}

function GalleryModal({ booking, onClose }: { booking: StayBooking, onClose: () => void }): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0)
  const activePhoto = booking.photos[activeIndex] ?? booking.photos[0]

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  if (!activePhoto) {
    return <></>
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Room photos for ${booking.hotel}`}
      className="fixed inset-0 z-50 flex items-center justify-center bg-catch-black/75 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) {
          onClose()
        }
      }}
    >
      <div className="w-full max-w-5xl overflow-hidden rounded-2xl bg-catch-white shadow-soft-lg">
        <div className="flex items-center justify-between border-b border-catch-beige-dark px-4 py-3 sm:px-5">
          <div>
            <p className="font-display text-lg font-medium">{booking.room}</p>
            <p className="text-xs text-gray-500">{booking.hotel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close room photos"
            className="rounded-full p-2 text-gray-500 hover:bg-catch-grey hover:text-catch-black"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="bg-catch-black">
          <img
            src={activePhoto.src}
            alt={activePhoto.alt}
            className="h-[52vh] min-h-[320px] w-full object-contain"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto p-3 sm:p-4">
          {booking.photos.map((photo, index) => (
            <button
              key={photo.src}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`Show room photo ${index + 1}`}
              className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${
                activeIndex === index ? 'border-catch-red' : 'border-transparent'
              }`}
            >
              <img src={photo.src} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function TripsPageView({ data }: { data: TripsPageData }): JSX.Element {
  const [period, setPeriod] = useState<Period>('upcoming')
  const [filter, setFilter] = useState<BookingFilter>('all')
  const [galleryBooking, setGalleryBooking] = useState<StayBooking | null>(null)
  const { assistant, ownerFirstName, trips } = data

  const visibleTrips = useMemo(
    () => trips
      .filter((trip) => trip.period === period)
      .map((trip) => ({
        ...trip,
        bookings: trip.bookings.filter(
          (booking) => filter === 'all' || booking.kind === filter
        )
      }))
      .filter((trip) => trip.bookings.length > 0),
    [filter, period, trips]
  )

  const upcomingTrips = trips.filter((trip) => trip.period === 'upcoming')
  const upcomingCount = upcomingTrips.length
  const pastCount = trips.filter((trip) => trip.period === 'past').length
  const nextTrip = upcomingTrips[0]

  return (
    <div className="min-h-screen bg-catch-white text-catch-black font-sans">
      <header className="sticky top-0 z-20 border-b border-catch-beige-dark bg-catch-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-4 sm:px-6">
          <CatchLogo size="sm" />
          <button
            type="button"
            onClick={() => window.print()}
            className="flex items-center gap-2 rounded-full border border-catch-beige-dark bg-white px-3.5 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-catch-red/40 hover:text-catch-black"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Save itinerary</span>
            <span className="sm:hidden">Save</span>
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 pb-20 pt-10 sm:px-6 sm:pt-14">
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="flex items-center gap-3">
              <AssistantAvatar assistant={assistant} size="md" />
              <div>
                <p className="text-sm font-medium text-catch-black">{assistant.name}</p>
                <p className="text-xs text-gray-500">Your assistant</p>
              </div>
            </div>
            <h1 className="mt-4 max-w-2xl font-display text-4xl sm:text-5xl">
              Everything I&apos;ve booked for you
            </h1>
            <p className="mt-3 max-w-xl text-gray-600">
              {ownerFirstName ? `Hi ${ownerFirstName}. ` : ''}I put your flights, hotels and room details here. I&apos;ll keep it tidy if anything changes.
            </p>
          </div>
          {period === 'upcoming' && nextTrip && (
            <div className="rounded-2xl border border-catch-red/20 bg-catch-red/5 px-4 py-4 sm:max-w-[250px]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-catch-red" />
                <p className="text-[11px] uppercase tracking-[0.12em] text-gray-500">You&apos;re all set</p>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-gray-700">
                I&apos;ve kept your {nextTrip.destination} bookings together. {nextTrip.startsIn ?? 'Everything is ready when you need it.'}
              </p>
            </div>
          )}
        </div>

        <div className="mt-9 border-b border-catch-beige-dark">
          <div className="flex gap-6">
            {([
              { value: 'upcoming' as const, label: 'Upcoming', count: upcomingCount },
              { value: 'past' as const, label: 'Past', count: pastCount }
            ]).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => setPeriod(tab.value)}
                className={`relative pb-3 text-sm font-medium transition-colors ${
                  period === tab.value ? 'text-catch-black' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {tab.label}
                <span className="ml-1.5 text-xs text-gray-400">{tab.count}</span>
                {period === tab.value && (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-catch-red" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="sticky top-16 z-10 -mx-4 flex items-center gap-2 border-b border-catch-beige-dark bg-catch-white/95 px-4 py-3 backdrop-blur sm:mx-0 sm:border-0 sm:px-0">
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            All bookings
          </FilterButton>
          <FilterButton active={filter === 'flight'} onClick={() => setFilter('flight')}>
            <span className="inline-flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> Flights</span>
          </FilterButton>
          <FilterButton active={filter === 'stay'} onClick={() => setFilter('stay')}>
            <span className="inline-flex items-center gap-1.5"><BedDouble className="h-3.5 w-3.5" /> Stays</span>
          </FilterButton>
        </div>

        <div className="mt-8 space-y-12">
          {visibleTrips.map((trip) => (
            <section key={trip.id} aria-labelledby={`${trip.id}-heading`}>
              <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 id={`${trip.id}-heading`} className="font-display text-2xl font-medium">
                      {trip.destination}
                    </h2>
                    {trip.startsIn && (
                      <span className="rounded-full bg-catch-beige px-2 py-0.5 text-[11px] font-medium text-gray-600">
                        {trip.startsIn}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {trip.country} · {trip.dateRange}
                  </p>
                  <p className="mt-2 max-w-2xl text-sm text-gray-600">
                    {trip.assistantNote}
                  </p>
                </div>
                <span className="flex w-fit items-center gap-1.5 text-sm font-medium text-green-700">
                  <Check className="h-4 w-4" /> {trip.bookings.filter((booking) => booking.status === 'confirmed').length} confirmed
                </span>
              </div>

              <div className="relative space-y-4 before:absolute before:bottom-4 before:left-[18px] before:top-4 before:w-px before:bg-catch-beige-dark sm:space-y-5 sm:before:left-[22px]">
                {trip.bookings.map((booking) => (
                  <div key={booking.id} className="relative grid grid-cols-[36px_1fr] gap-3 sm:grid-cols-[44px_1fr] sm:gap-4">
                    <div className="relative z-[1] mt-5 flex h-9 w-9 items-center justify-center rounded-full border border-catch-beige-dark bg-catch-white text-catch-red shadow-soft sm:h-11 sm:w-11">
                      {booking.kind === 'flight'
                        ? <Plane className="h-4 w-4 sm:h-5 sm:w-5" />
                        : <BedDouble className="h-4 w-4 sm:h-5 sm:w-5" />}
                    </div>
                    {booking.kind === 'flight'
                      ? <FlightCard assistant={assistant} booking={booking} />
                      : (
                        <StayCard
                          assistant={assistant}
                          booking={booking}
                          onOpenGallery={() => setGalleryBooking(booking)}
                        />
                        )}
                  </div>
                ))}
              </div>
            </section>
          ))}
          {visibleTrips.length === 0 && (
            <div className="rounded-2xl border border-dashed border-catch-beige-dark bg-catch-grey/45 px-6 py-14 text-center">
              <p className="font-display text-xl font-medium">Nothing here yet</p>
              <p className="mt-2 text-sm text-gray-500">
                I don&apos;t have any {period} {filter === 'all' ? 'bookings' : `${filter}s`} to show you.
              </p>
            </div>
          )}
        </div>

        <div className="mt-14 rounded-2xl border border-catch-beige-dark bg-catch-grey/60 p-5 text-center sm:p-6">
          <div className="mx-auto mb-3 w-fit"><AssistantAvatar assistant={assistant} size="md" /></div>
          <p className="font-display text-lg font-medium">Need me to change anything?</p>
          <p className="mt-1 text-sm text-gray-500">
            Reply in your chat with {assistant.name} and I&apos;ll take care of the booking details.
          </p>
        </div>
      </main>

      <footer className="border-t border-catch-beige-dark px-4 py-5 text-center text-xs text-gray-400">
        Booked and kept tidy by {assistant.name} at Catch
      </footer>

      {galleryBooking && (
        <GalleryModal booking={galleryBooking} onClose={() => setGalleryBooking(null)} />
      )}
    </div>
  )
}

function TripsPageShell({
  title,
  message
}: {
  title: string
  message: string
}): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-catch-white text-catch-black font-sans">
      <header className="border-b border-catch-beige-dark">
        <div className="mx-auto flex h-16 w-full max-w-5xl items-center px-4 sm:px-6">
          <CatchLogo size="sm" />
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-2xl flex-1 items-center justify-center px-6 py-20 text-center">
        <div>
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-catch-red/10 text-catch-red">
            <Plane className="h-5 w-5" />
          </div>
          <h1 className="mt-5 font-display text-3xl">{title}</h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-500">{message}</p>
        </div>
      </main>
    </div>
  )
}

export function TripsPage(): JSX.Element {
  const { token } = useParams<{ token: string }>()
  const [data, setData] = useState<TripsPageData | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load(): Promise<void> {
      if (!token) {
        setFailed(true)
        return
      }
      try {
        const response = await getTripsApiV1TripsTokenGet({
          path: { token },
          baseURL: API_BASE_URL,
          throwOnError: false
        })
        if (cancelled) {
          return
        }
        if (response.error || !response.data) {
          setFailed(true)
          return
        }
        setData(adaptTripsPage(response.data))
      } catch {
        if (!cancelled) {
          setFailed(true)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [token])

  if (failed) {
    return (
      <TripsPageShell
        title="This trips page is no longer available"
        message="Ask your assistant for a fresh trips link and I’ll generate one for you."
      />
    )
  }
  if (!data) {
    return (
      <TripsPageShell
        title="Gathering everything I booked"
        message="I’m pulling in your latest flight, hotel and room details now."
      />
    )
  }
  return <TripsPageView data={data} />
}

export function TripsMockPage(): JSX.Element {
  return <TripsPageView data={MOCK_PAGE_DATA} />
}
